import React, { useState } from 'react';
import { observer } from 'mobx-lite';
import {
    Box,
    Typography,
    Button,
    TextField,
    Card,
    CardContent,
    IconButton,
    Stack,
    Divider,
    Dialog,
    DialogContent,
    DialogActions,
    Slider,
    Tooltip,
    Chip,
    useTheme,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { Add, Delete, ContentCopy, Edit, PaletteOutlined, WarningAmber } from '@mui/icons-material';
import { MainStoreContextItem } from '../../Stores/MainStore';
import { EventTheme } from '../../Stores/EventThemesStore';
import Color from 'color';

const inputSx = (accentColor: string) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        transition: 'box-shadow 0.2s ease',
        '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${Color(accentColor).alpha(0.15).hexa()}`,
        },
    },
});

export const EventThemesManager: React.FC = observer(() => {
    const store = MainStoreContextItem.useStore();
    const { eventThemesStore } = store;
    const muiTheme = useTheme();
    const accent = muiTheme.palette.primary.main;

    const [editingTheme, setEditingTheme] = useState<EventTheme | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [modalOpened, setModalOpened] = useState(false);
    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [themeToDelete, setThemeToDelete] = useState<EventTheme | null>(null);

    const paperBg = Color(muiTheme.palette.background.paper).mix(Color('#fff'), 0.10).alpha(0.85).hexa();

    const handleSave = () => {
        if (editingTheme) {
            if (isCreating) {
                eventThemesStore.addTheme({
                    name: editingTheme.name,
                    message: editingTheme.message,
                    speechMessage: editingTheme.speechMessage,
                    eventClassification: editingTheme.eventClassification,
                    color: editingTheme.color,
                    deniesAccess: editingTheme.deniesAccess ?? false,
                });
                setIsCreating(false);
            } else {
                eventThemesStore.updateTheme(editingTheme.id, {
                    name: editingTheme.name,
                    message: editingTheme.message,
                    speechMessage: editingTheme.speechMessage,
                    eventClassification: editingTheme.eventClassification,
                    color: editingTheme.color,
                    deniesAccess: editingTheme.deniesAccess ?? false,
                });
            }
            setEditingTheme(null);
            setModalOpened(false);
        }
    };

    const handleCancel = () => {
        setEditingTheme(null);
        setIsCreating(false);
        setModalOpened(false);
    };

    const handleCreate = () => {
        setEditingTheme({
            id: '',
            name: '',
            message: '',
            speechMessage: '',
            eventClassification: '',
            color: { r: 100, g: 100, b: 100, a: 0.8 },
        });
        setIsCreating(true);
        setModalOpened(true);
    };

    const handleEdit = (theme: EventTheme) => {
        setEditingTheme({ ...theme });
        setIsCreating(false);
        setModalOpened(true);
    };

    const handleDelete = (theme: EventTheme) => {
        setThemeToDelete(theme);
        setDeleteModalOpened(true);
    };

    const confirmDelete = () => {
        if (themeToDelete) {
            eventThemesStore.removeTheme(themeToDelete.id);
            setThemeToDelete(null);
            setDeleteModalOpened(false);
        }
    };

    const cancelDelete = () => {
        setThemeToDelete(null);
        setDeleteModalOpened(false);
    };

    const handleDuplicate = (id: string) => {
        eventThemesStore.duplicateTheme(id);
    };

    const updateEditingTheme = (field: keyof EventTheme, value: any) => {
        if (editingTheme) {
            setEditingTheme({ ...editingTheme, [field]: value });
        }
    };

    const updateColor = (field: keyof EventTheme['color'], value: number) => {
        if (editingTheme) {
            setEditingTheme({
                ...editingTheme,
                color: { ...editingTheme.color, [field]: value },
            });
        }
    };

    const rgbaToHex = (r: number, g: number, b: number, _a: number = 1): string => {
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    const hexToRgba = (hex: string): { r: number; g: number; b: number; a: number } => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (result) {
            return {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16),
                a: 0.8,
            };
        }
        return { r: 100, g: 100, b: 100, a: 0.8 };
    };

    const handleColorChange = (color: string) => {
        if (editingTheme) {
            const rgba = hexToRgba(color);
            setEditingTheme({
                ...editingTheme,
                color: rgba,
            });
        }
    };

    const btnContainedSx = {
        borderRadius: 2,
        textTransform: 'none' as const,
        fontWeight: 600,
        px: 2.5,
        boxShadow: `0 2px 12px ${Color(accent).alpha(0.3).hexa()}`,
        '&:hover': { boxShadow: `0 4px 20px ${Color(accent).alpha(0.4).hexa()}` },
    };

    const btnOutlinedSx = {
        borderRadius: 2,
        textTransform: 'none' as const,
        fontWeight: 500,
        px: 2.5,
        borderColor: Color('#fff').alpha(0.15).hexa(),
        '&:hover': { borderColor: Color('#fff').alpha(0.3).hexa(), backgroundColor: Color('#fff').alpha(0.04).hexa() },
    };

    const dialogPaperSx = {
        backgroundColor: paperBg,
        backdropFilter: 'blur(20px)',
        backgroundImage: 'none',
        boxShadow: `0 24px 80px rgba(0,0,0,0.7), 0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 ${Color('#fff').alpha(0.06).hexa()}`,
        borderRadius: 3,
        border: `1px solid ${Color('#fff').alpha(0.08).hexa()}`,
    };

    return (
        <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                        Event Themes
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Customize display overlays for different event classifications
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add sx={{ fontSize: 16 }} />}
                    onClick={handleCreate}
                    sx={btnContainedSx}
                >
                    Add Theme
                </Button>
            </Box>

            {/* Theme cards */}
            <Box sx={{ height: '60vh', overflow: 'auto', pr: 0.5 }}>
                <Stack spacing={1.5}>
                    {eventThemesStore.themes.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 6, opacity: 0.5 }}>
                            <PaletteOutlined sx={{ fontSize: 48, mb: 1, color: 'text.disabled' }} />
                            <Typography color="text.secondary">No themes configured yet</Typography>
                            <Typography variant="caption" color="text.disabled">Click "Add Theme" to create one</Typography>
                        </Box>
                    )}

                    {eventThemesStore.themes.map((theme) => {
                        const themeColorStr = eventThemesStore.getColorString(theme);
                        const themeColor = Color(`rgb(${theme.color.r}, ${theme.color.g}, ${theme.color.b})`);
                        return (
                            <Card
                                key={theme.id}
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    borderColor: 'divider',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: themeColor.alpha(0.5).hexa(),
                                        boxShadow: `0 2px 12px ${themeColor.alpha(0.15).hexa()}`,
                                    },
                                }}
                            >
                                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {/* Color swatch */}
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 2,
                                                backgroundColor: themeColorStr,
                                                border: `1px solid ${Color('#fff').alpha(0.15).hexa()}`,
                                                flexShrink: 0,
                                                boxShadow: `0 2px 8px ${themeColor.alpha(0.3).hexa()}`,
                                            }}
                                        />

                                        {/* Content */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                                <Typography variant="body1" fontWeight={600} noWrap>
                                                    {theme.name}
                                                </Typography>
                                                {theme.eventClassification && (
                                                    <Chip
                                                        label={theme.eventClassification}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.7rem',
                                                            fontWeight: 500,
                                                            backgroundColor: themeColor.alpha(0.12).hexa(),
                                                            color: themeColor.lightness() < 40 ? themeColor.lighten(0.6).hex() : themeColor.hex(),
                                                            borderRadius: 1,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {theme.message}
                                            </Typography>
                                            {theme.speechMessage && (
                                                <Typography variant="caption" color="text.disabled" noWrap sx={{ display: 'block' }}>
                                                    Speech: {theme.speechMessage}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Actions */}
                                        <Stack direction="row" spacing={0.25}>
                                            <Tooltip title="Duplicate" arrow>
                                                <IconButton
                                                    onClick={() => handleDuplicate(theme.id)}
                                                    size="small"
                                                    sx={{ color: 'text.secondary', '&:hover': { color: accent } }}
                                                >
                                                    <ContentCopy sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit" arrow>
                                                <IconButton
                                                    onClick={() => handleEdit(theme)}
                                                    size="small"
                                                    sx={{ color: 'text.secondary', '&:hover': { color: accent } }}
                                                >
                                                    <Edit sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete" arrow>
                                                <IconButton
                                                    onClick={() => handleDelete(theme)}
                                                    size="small"
                                                    sx={{ color: 'text.secondary', '&:hover': { color: muiTheme.palette.error.main } }}
                                                >
                                                    <Delete sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>
            </Box>

            {/* Edit/Create Dialog */}
            <Dialog
                open={modalOpened}
                onClose={handleCancel}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    backdrop: { sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.6)' } },
                    paper: { sx: dialogPaperSx },
                }}
            >
                {/* Dialog header */}
                <Box sx={{ pt: 3, pb: 1, px: 3, textAlign: 'center' }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            mx: 'auto',
                            mb: 1.5,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${Color(accent).alpha(0.2).hexa()}, ${Color(accent).alpha(0.05).hexa()})`,
                            border: `1px solid ${Color(accent).alpha(0.3).hexa()}`,
                        }}
                    >
                        {isCreating
                            ? <Add sx={{ fontSize: 24, color: accent }} />
                            : <Edit sx={{ fontSize: 22, color: accent }} />
                        }
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
                        {isCreating ? 'Create New Theme' : 'Edit Theme'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {isCreating ? 'Define a new event overlay theme' : 'Modify the selected theme'}
                    </Typography>
                </Box>

                <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                    <Stack spacing={2}>
                        <TextField
                            label="Name"
                            value={editingTheme?.name || ''}
                            onChange={(e) => updateEditingTheme('name', e.target.value)}
                            placeholder="Theme name"
                            size="small"
                            fullWidth
                            sx={inputSx(accent)}
                        />

                        <TextField
                            label="Message"
                            value={editingTheme?.message || ''}
                            onChange={(e) => updateEditingTheme('message', e.target.value)}
                            placeholder="Display message"
                            size="small"
                            fullWidth
                            sx={inputSx(accent)}
                        />

                        <TextField
                            label="Speech Message"
                            value={editingTheme?.speechMessage || ''}
                            onChange={(e) => updateEditingTheme('speechMessage', e.target.value)}
                            placeholder="Optional: Message for text-to-speech"
                            helperText="Spoken when the event occurs. If empty, the display message is used."
                            size="small"
                            fullWidth
                            sx={inputSx(accent)}
                        />

                        <TextField
                            label="Event Classification"
                            value={editingTheme?.eventClassification || ''}
                            onChange={(e) => updateEditingTheme('eventClassification', e.target.value)}
                            placeholder="e.g., recognized, unknown, no-match"
                            size="small"
                            fullWidth
                            sx={inputSx(accent)}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={editingTheme?.deniesAccess ?? false}
                                    onChange={(e) => updateEditingTheme('deniesAccess', e.target.checked)}
                                />
                            }
                            label="Denies Access (Guard Mode: show person but do not record IN/OUT)"
                        />

                        <Divider sx={{ pt: 0.5 }}>
                            <Chip label="Color" size="small" sx={{ fontWeight: 500, fontSize: '0.75rem' }} />
                        </Divider>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                                <MuiColorInput
                                    value={editingTheme ? rgbaToHex(editingTheme.color.r, editingTheme.color.g, editingTheme.color.b) : '#646464'}
                                    onChange={handleColorChange}
                                    format="hex"
                                    size="small"
                                    fullWidth
                                    sx={inputSx(accent)}
                                />
                            </Box>
                            {/* Live preview swatch */}
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 2,
                                    backgroundColor: editingTheme ? eventThemesStore.getColorString(editingTheme) : 'transparent',
                                    border: `1px solid ${Color('#fff').alpha(0.15).hexa()}`,
                                    boxShadow: editingTheme
                                        ? `0 4px 16px ${Color(`rgb(${editingTheme.color.r}, ${editingTheme.color.g}, ${editingTheme.color.b})`).alpha(0.4).hexa()}`
                                        : 'none',
                                    flexShrink: 0,
                                    transition: 'all 0.2s ease',
                                }}
                            />
                        </Box>

                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" fontWeight={500}>Opacity</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                                    {((editingTheme?.color.a || 0) * 100).toFixed(0)}%
                                </Typography>
                            </Box>
                            <Slider
                                value={editingTheme?.color.a || 0.8}
                                onChange={(_, value) => updateColor('a', value as number)}
                                min={0}
                                max={1}
                                step={0.05}
                                marks={[
                                    { value: 0, label: '0%' },
                                    { value: 0.5, label: '50%' },
                                    { value: 1, label: '100%' },
                                ]}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => `${(v * 100).toFixed(0)}%`}
                            />
                        </Box>

                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', textAlign: 'center' }}>
                            {editingTheme
                                ? `rgba(${editingTheme.color.r}, ${editingTheme.color.g}, ${editingTheme.color.b}, ${editingTheme.color.a})`
                                : ''}
                        </Typography>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1 }}>
                    <Button variant="outlined" onClick={handleCancel} sx={btnOutlinedSx}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} sx={btnContainedSx}>
                        {isCreating ? 'Create' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteModalOpened}
                onClose={cancelDelete}
                maxWidth="xs"
                fullWidth
                slotProps={{
                    backdrop: { sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.6)' } },
                    paper: { sx: dialogPaperSx },
                }}
            >
                <Box sx={{ pt: 3, pb: 1, px: 3, textAlign: 'center' }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            mx: 'auto',
                            mb: 1.5,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${Color(muiTheme.palette.error.main).alpha(0.2).hexa()}, ${Color(muiTheme.palette.error.main).alpha(0.05).hexa()})`,
                            border: `1px solid ${Color(muiTheme.palette.error.main).alpha(0.3).hexa()}`,
                        }}
                    >
                        <WarningAmber sx={{ fontSize: 24, color: muiTheme.palette.error.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
                        Delete Theme
                    </Typography>
                </Box>
                <DialogContent sx={{ px: 3, pt: 1, pb: 1, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete <strong>"{themeToDelete?.name}"</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1, justifyContent: 'center' }}>
                    <Button variant="outlined" onClick={cancelDelete} sx={btnOutlinedSx}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={confirmDelete}
                        sx={{
                            ...btnContainedSx,
                            boxShadow: `0 2px 12px ${Color(muiTheme.palette.error.main).alpha(0.3).hexa()}`,
                            '&:hover': { boxShadow: `0 4px 20px ${Color(muiTheme.palette.error.main).alpha(0.4).hexa()}` },
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
});
